import { useState } from "react";
import { isFileAccepted } from "@/utils/helper";
import { FileText, ImageIcon } from "lucide-react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

interface FileUploaderProps {
  name?: string;
  file: File | string | null;
  onFileChange: (file: File | null) => void;
  accept?: string;
  placeholder?: string;
  children?: React.ReactNode;
}
const FileUploader = ({
  name = "file-input",
  file,
  children,
  onFileChange,
  accept = "image/*",
  placeholder,
}: FileUploaderProps) => {
  const { t } = useTranslation(["common"]);
  const [isDragOver, setIsDragOver] = useState(false);

  const placeholderText =
    placeholder ?? t("common:file_upload.drag_drop_or_browse_plain");

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0] || null;
    if (file && !isFileAccepted(file, accept)) {
      toast.error(t("common:file_upload.file_type_not_accepted"));
      return;
    }
    onFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onFileChange(file);
  };

  const renderFile = () => {
    if (!file) return null;

    let ext = "";
    let url = "";
    let displayName = "";

    if (typeof file === "string") {
      // Get only the filename, not the full URL/path
      const parts = file.split("/");
      displayName = parts[parts.length - 1] || file;
      ext = displayName.split(".").pop() || "";
      url = file;
    } else {
      displayName = file.name;
      ext = file.name.split(".").pop() || "";
      url = URL.createObjectURL(file);
    }

    const imgTypes = ["jpg", "jpeg", "png", "webp", "svg"];
    if (imgTypes.includes(ext.toLowerCase())) {
      return (
        <img
          src={url}
          alt={displayName}
          className="w-full h-full object-cover"
        />
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        download={displayName}
        className="text-primary underline flex items-center gap-2"
      >
        <FileText className="w-5 h-5" />
        <span className="text-sm text-primary">{displayName}</span>
      </a>
    );
  };

  return (
    <div>
      <label
        htmlFor={name}
        className={clsx(
          "flex flex-col items-center justify-center border-2 border-dashed rounded-2xl w-full h-64 cursor-pointer transition overflow-hidden bg-primary-muted border-info hover:bg-primary-muted",
          isDragOver && "bg-surface-elevated border-info",
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => onFileChange(null)}
      >
        {file ? (
          <div className="w-full h-full flex items-center justify-center relative group">
            {renderFile()}
            <div className="absolute bottom-3 flex gap-2 text-primary p-1 px-3 rounded-md bg-white opacity-0 group-hover:opacity-60 transition-opacity duration-200">
              <ImageIcon className="w-5 h-5" />
              <span className="text-sm">
                {t("common:file_upload.click_to_replace")}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center flex-col justify-center gap-2">
            <ImageIcon className="w-12 h-12 text-info" />
            <p className="text-gray-700">{placeholderText}</p>
            {children}
          </div>
        )}
      </label>
      <input
        type="file"
        id={name}
        accept={accept}
        hidden
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileUploader;
